import React from 'react';
import { Box, Button, Flex, Icon, IconButton, Text, Heading, useSnackbar } from 'pounceV2';
import { extractErrorMessage, getElapsedTime } from 'Helpers/utils';
import { Notification as NotificationType } from 'Generated/schema';
import NotificationActionsV3 from 'Components/Notifications/NotificationActionsV3';
import dayjs from 'dayjs';
import useHover from 'Hooks/useHover';
import { useTheme } from '@mui/material';
import type { IconType } from 'pounceV2/src/theme/icons';
import { notificationsIcons } from './constants';
import { useUpdateNotification } from './graphql/UpdateNotification.generated';
import { FormatMessage } from './utils';

const NotificationV3 = ({ notification }: { notification: NotificationType }) => {
  const theme = useTheme();
  const { isHovering, handlers: hoverHandlers } = useHover();
  const { pushSnackbar } = useSnackbar();

  const [updateNotification] = useUpdateNotification({
    onError: error => {
      pushSnackbar({
        variant: 'error',
        title: extractErrorMessage(error),
      });
    },
  });

  const now = dayjs().utc().toISOString();
  const archived = Date.parse(notification.archivedAt) > 0;
  const read = Date.parse(notification.readAt) > 0;
  const icon = notificationsIcons[notification.level] ?? {
    type: 'notifications-info',
    color: theme.palette.tokens.content.assets.semantic.info.base,
  };
  return (
    <Flex
      align="flex-start"
      justify="space-between"
      gap={5}
      px={6}
      py={4}
      borderBottom="1px solid"
      borderBottomColor={theme.palette.tokens.stroke.neutral[5]}
      backgroundColor={
        read && !archived
          ? theme.palette.tokens.background.neutral.inverse.raised[4]
          : theme.palette.tokens.background.neutral.mid[3]
      }
      {...hoverHandlers}
    >
      <Icon type={icon.type as IconType} color={icon.color as string} />

      <Flex align="flex-start" direction="column" gap={0} flex={1}>
        <Flex align="flex-start" justify="space-between" width="100%" mb={1}>
          <Box flex={1} mr={3}>
            <Text
              variant="label/lg"
              fontWeight="semibold"
              color={
                read || archived
                  ? theme.palette.tokens.content.text.neutral[3]
                  : theme.palette.tokens.content.text.neutral.base
              }
            >
              {notification.title}
            </Text>

            <Text
              variant="label/sm"
              color={theme.palette.tokens.content.text.accent[14][3]}
              fontWeight="semibold"
              mt={1}
            >
              less than {getElapsedTime(new Date(notification.createdAt).getTime() / 1000)}
            </Text>
          </Box>

          {isHovering && !archived && (
            <Flex align="center" spacing={2}>
              <IconButton
                variant="outline"
                icon="archive"
                size="small"
                title="Archive Notification"
                aria-label="Archive Notification"
                onClick={() => {
                  updateNotification({
                    variables: {
                      input: { id: notification.id, archivedAt: now },
                    },
                  });
                }}
              />
              {read ? (
                <IconButton
                  variant="outline"
                  icon="mark-as-unread"
                  size="small"
                  title="Mark Notification As Unread"
                  aria-label="Mark Notification As Unread"
                  onClick={() => {
                    updateNotification({
                      variables: {
                        input: { id: notification.id, readAt: '0001-01-01T00:00:00Z' },
                      },
                    });
                  }}
                />
              ) : (
                <IconButton
                  variant="outline"
                  icon="mark-as-read"
                  size="small"
                  title="Mark Notification As Read"
                  aria-label="Mark Notification As Read"
                  onClick={() => {
                    updateNotification({
                      variables: {
                        input: { id: notification.id, readAt: now },
                      },
                    });
                  }}
                />
              )}
            </Flex>
          )}
        </Flex>

        <Heading
          variant="h6"
          wordBreak="break-word"
          mb={4}
          color={
            read || archived
              ? theme.palette.tokens.content.text.neutral[3]
              : theme.palette.tokens.content.text.neutral.base
          }
        >
          <FormatMessage message={notification.message} />
        </Heading>
        {!archived ? (
          <NotificationActionsV3 notification={notification} />
        ) : (
          <Button
            variant="outline"
            size="small"
            onClick={() => {
              updateNotification({
                variables: {
                  input: { id: notification.id, archivedAt: '0001-01-01T00:00:00Z' },
                },
              });
            }}
          >
            Unarchive
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

export default NotificationV3;
